import React, { useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import { post } from 'axios';
import PropTypes from 'prop-types';

import FilledButton from 'components/Buttons/FilledButton';
import ModalContiner from 'components/ModalContainer';

import { renderToast } from 'components/Informations/Toast/utils';

import endpoints from 'services/endpoints';

import * as Yup from 'yup';
import * as S from './styles';

const { insertQuestion, updateQuestion } = endpoints;

export default function ModalQuestions({ type, data, open, setOpen }) {
  const {
    id_questao,
    id_area,
    id_bloco,
    nome,
    descricao,
    id_funcionario: idFuncionario,
    id_tipo_questao,
    observer,
  } = data;

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    autoClose: 6000,
  });

  const ModalTitle = useMemo(
    () => (type === 'add' ? 'Adicionar nova questão' : 'Editar questão'),
    [type]
  );

  const initialValues = useMemo(
    () =>
      type === 'add'
        ? {
            selected: { label: '', value: '' },
            nome: '',
            descricao: '',
          }
        : {
            selected: data?.selected,
            nome,
            descricao,
          },
    [type, nome, descricao, data]
  );

  const selected = useMemo(
    () => data.options?.find(o => o?.label === data.selected?.label),
    [data]
  );

  const schema = Yup.object().shape({
    selected: Yup.object()
      .shape({
        value: Yup.number().required('Selecione uma opção'),
        label: Yup.string(),
      })
      .required('Selecione uma opção'),
    nome: Yup.string()
      .min(5, 'Dados insuficientes')
      .max(255, 'Limite excedido')
      .required('Campo obrigatório'),
    descricao: Yup.string()
      .min(5, 'Dados insuficientes')
      .max(255, 'Limite excedido')
      .nullable(),
  });

  const handleSubmit = (values, { resetForm }) => {
    if (type === 'add') {
      const objectToSend = {
        id_usr_in: idFuncionario,
        id_area,
        id_bloco: values.selected?.value,
        id_tipo_questao: 1,
        nome: values.nome,
        descricao: values.descricao,
      };

      return post(insertQuestion, objectToSend)
        .then(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'success',
            message: 'Questão inserida com sucesso',
            autoClose: 6000,
          });
        })
        .then(() => {
          setTimeout(() => {
            resetForm();
            observer();
            setOpen(false);
          }, 4000);
        })
        .catch(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'error',
            message: 'Erro ao remover questão',
            autoClose: 6000,
          });
        });
    }

    const objectToSend = {
      id_bloco,
      id_usr_up: idFuncionario,
      id_questao,
      id_tipo_questao,
      nome: values.nome,
      descricao: values.descricao,
    };

    return post(updateQuestion, objectToSend)
      .then(() => {
        setToastVisibility(true);
        setToastInfos({
          type: 'success',
          message: 'Questão atualizada com sucesso',
          autoClose: 6000,
        });
      })
      .then(() => {
        setTimeout(() => {
          resetForm();
          observer();
          setOpen(false);
        }, 4000);
      })
      .catch(() => {
        setToastVisibility(true);
        setToastInfos({
          type: 'error',
          message: 'Erro ao atualizar questão',
          autoClose: 6000,
        });
      });
  };

  return (
    <>
      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.autoClose,
          setToastVisibility
        )}

      <ModalContiner isOpen={open} setIsOpen={setOpen} headText={ModalTitle}>
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, setFieldValue, resetForm }) => (
            <Form>
              <S.FormField
                label_for="block-options"
                label="Bloco"
                error={!!errors?.selected}
                error_message={errors?.selected?.value}
                value={values?.selected}
              >
                <S.SelectField
                  id="block-options"
                  name="selected"
                  error={!!errors?.selected}
                  onChange={e =>
                    setFieldValue('selected', JSON.parse(e.target.value))
                  }
                >
                  <option
                    disabled
                    selected={!selected}
                    label="Selecione uma opção"
                    value={JSON.stringify({})}
                  />
                  {data.options?.map(o => (
                    <option
                      value={JSON.stringify(o)}
                      label={o.label}
                      selected={o.label === selected?.label}
                    />
                  ))}
                </S.SelectField>
              </S.FormField>
              <S.FormField
                label_for="nome"
                label="Ferramenta/Tecnologia/Metodologia"
                error={!!errors?.nome}
                error_message={errors?.nome}
              >
                <S.FormInput
                  value={values.nome}
                  id="nome"
                  name="nome"
                  error={!!errors?.nome}
                  onChange={e => setFieldValue('nome', e.target.value)}
                />
              </S.FormField>
              <S.FormField
                label_for="descricao"
                label="Descrição"
                error={!!errors?.descricao}
                error_message={errors?.descricao}
              >
                <S.TextField
                  id="descricao"
                  name="descricao"
                  error={!!errors?.descricao}
                  onChange={e => setFieldValue('descricao', e.target.value)}
                />
              </S.FormField>
              <S.ButtonsWrap>
                <FilledButton
                  text="Cancelar"
                  buttonClass="cancel"
                  handleClick={e => {
                    e.preventDefault();
                    resetForm();
                    setOpen(false);
                  }}
                />
                <FilledButton
                  text="enviar"
                  buttonType="submit"
                  buttonClass="confirm"
                />
              </S.ButtonsWrap>
            </Form>
          )}
        </Formik>
      </ModalContiner>
    </>
  );
}

ModalQuestions.defaultProps = {
  data: {
    options: [{ label: '', value: NaN }],
    selected: {},
    question: '',
    description: '',
  },
};

ModalQuestions.propTypes = {
  type: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  data: PropTypes.shape({
    question: PropTypes.string,
    description: PropTypes.string,
    selected: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    }),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.number,
      })
    ),
  }),
};
